import { BatchRun, BatchStatus, NodeRun, NodeRunStatus, RecipeRun, RecipeRunStatus } from '@/types/batch.types';

export const isAnyBatch = (batches: BatchRun[], status: RecipeRunStatus) =>
  batches.some((batch) => isAnyRun(batch, status));

export const isAnyRun = (batch: BatchRun, status: RecipeRunStatus) =>
  batch.recipeRuns.some((run) => run.status === status);

export const getFailedRun = (batch: BatchRun) => batch.recipeRuns.find((run) => run.status === 'FAILED');

const isAnyNodeRunInAnyBatch = (batch: BatchRun, status: NodeRunStatus) =>
  batch.recipeRuns.some((run) => run.nodeRuns.some((nodeRun) => nodeRun.status === status));

export const getBatchStatus = (batch: BatchRun): BatchStatus => {
  if (isAnyRun(batch, 'CANCELED')) {
    return 'CANCELED';
  }
  if (isAnyNodeRunInAnyBatch(batch, 'RUNNING') || isAnyNodeRunInAnyBatch(batch, 'PENDING')) {
    return 'RUNNING';
  }
  if (isAnyRun(batch, 'FAILED')) {
    return 'FAILED';
  }
  return 'COMPLETED';
};

export const getCountOf = (batch: BatchRun, nodeRunStatus: NodeRunStatus, filter?: (nodeId: string) => boolean) =>
  batch.recipeRuns.reduce((total, recipeRun) => {
    const completedNodeRuns = recipeRun.nodeRuns.filter(
      (nodeRun) => nodeRun.status === nodeRunStatus && (filter ? filter(nodeRun.nodeId) : true),
    ).length;
    return total + completedNodeRuns;
  }, 0);

/** Failed model runs with no `refundedAt` timestamp (missing, null, or empty). */
export const countFailedWithNoPlatformRefund = (batch: BatchRun, filter?: (nodeId: string) => boolean): number =>
  batch.recipeRuns.reduce((total, recipeRun) => {
    const n = recipeRun.nodeRuns.filter(
      (nodeRun) => nodeRun.status === 'FAILED' && !nodeRun.refundedAt && (filter ? filter(nodeRun.nodeId) : true),
    ).length;
    return total + n;
  }, 0);

export const getTotalNodeRuns = (batch: BatchRun, filter?: (nodeId: string) => boolean) =>
  batch.recipeRuns.reduce((total, recipeRun) => {
    const nodeRuns = filter ? recipeRun.nodeRuns.filter((nodeRun) => filter(nodeRun.nodeId)) : recipeRun.nodeRuns;
    return total + nodeRuns.length;
  }, 0);

export const getBatchesForNode = (nid: string, batches: BatchRun[]): BatchRun[] =>
  batches.filter((batch) => batch.recipeRuns.some((run) => run.nodeRuns.some((nodeRun) => nodeRun.nodeId === nid)));

export const isSomeNodesInRunningBatch = (nids: string[], batches: BatchRun[]): boolean => {
  for (const nid of nids) {
    const batchesForNode = getBatchesForNode(nid, batches);
    if (batchesForNode.some((batch) => getBatchStatus(batch) === 'RUNNING')) {
      return true;
    }
  }
  return false;
};

export const getNodeRunsInBatches = (nodeId: string, batches: BatchRun[]): NodeRun[] =>
  batches.flatMap((batch) =>
    batch.recipeRuns.flatMap((run) => run.nodeRuns.filter((nodeRun) => nodeRun.nodeId === nodeId)),
  );

export function areAllRunsFailedOrCompleted(recipeRun: RecipeRun): boolean {
  return recipeRun.nodeRuns.every((run) => run.status === 'FAILED' || run.status === 'COMPLETED');
}

export const getSingleRunProgress = (batch: BatchRun, filter?: (nodeId: string) => boolean): number | null => {
  for (const recipeRun of batch.recipeRuns) {
    for (const nodeRun of recipeRun.nodeRuns) {
      if ((nodeRun.status === 'RUNNING' || nodeRun.status === 'PENDING') && (filter ? filter(nodeRun.nodeId) : true)) {
        return nodeRun.progress ?? 0;
      }
    }
  }
  return null;
};
